import { createLazyFileRoute } from "@tanstack/react-router";

import { trpc } from "@blis/trpc/react";

import { ErrorPage } from "@/components/ErrorPage";
import { LoadingPage } from "@/components/LoadingPage";
import { BillingDetailShell } from "@/features/admin/components/billing/BillingDetailShell";
import { BillingRecordNotFound } from "@/features/admin/components/billing/BillingRecordNotFound";
import { MasterInvoicePreview } from "@/features/admin/components/billing/MasterInvoicePreview";
import { getTrpcErrorCode } from "@/utils/trpc";

export const Route = createLazyFileRoute(
  "/_auth/admin/billing/master-invoices/$id",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();

  const { data, error, isPending, isError } =
    trpc.admin.billing.getMasterInvoice.useQuery(
      { id },
      {
        retry(failureCount, err) {
          if (getTrpcErrorCode(err) === "NOT_FOUND") {
            return false;
          }
          return failureCount < 3;
        },
      },
    );

  if (isPending) {
    return (
      <BillingDetailShell
        billingTab="master-invoices"
        breadcrumbTail={`Master invoice ${id}`}
        title={`Master invoice ${id}`}
      >
        <LoadingPage />
      </BillingDetailShell>
    );
  }

  if (isError && getTrpcErrorCode(error) === "NOT_FOUND") {
    return (
      <BillingDetailShell
        billingTab="master-invoices"
        breadcrumbTail={`Master invoice ${id}`}
        title="Master invoice not found"
      >
        <BillingRecordNotFound
          resourceLabel="Master invoice"
          resourceId={id}
          billingTab="master-invoices"
        />
      </BillingDetailShell>
    );
  }

  if (isError) {
    return (
      <BillingDetailShell
        billingTab="master-invoices"
        breadcrumbTail={`Master invoice ${id}`}
        title="Unable to load master invoice"
      >
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-lg border border-destructive/25 bg-destructive/5 shadow-xs">
          <ErrorPage error={error} />
        </div>
      </BillingDetailShell>
    );
  }

  return (
    <BillingDetailShell
      billingTab="master-invoices"
      breadcrumbTail={`Master invoice ${data.document.id}`}
      title={`Master invoice ${data.document.id}`}
    >
      <MasterInvoicePreview data={data} />
    </BillingDetailShell>
  );
}
